import { Package } from 'lucide-react';
import type { EnrolmentPackageKind } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { dateOnly, money } from '@/lib/utils';
import { Badge, Card, CardContent, CardHeader, CardTitle, StatusBadge } from '@/components/ui/primitives';
import { packageKindLabel, packageKindVariant } from './PackagesTab';
import { packageStatusTone } from './PatientPackagesCard';
import { AllowanceUse } from './PackageLineBadge';

export interface BillPackage {
  id: string;
  code: string;
  name: string;
  kind: EnrolmentPackageKind;
  status: string;
  priceMinor: number;
  soldOnThisBill: boolean;
  expiresAt: string | null;
  allowanceMinor?: number;
  allowanceUsedMinor?: number;
  items: { id: string; description: string; includedQty: number; usedQty: number; remainingQty: number }[];
}

export function BillPackagesCard({ packages }: { packages: readonly BillPackage[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('billing', 'packages_on_this_bill')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {packages.map((pkg) => (
          <div key={pkg.id} className="space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="font-medium">{pkg.name}</span>
              <Badge variant={packageKindVariant(pkg.kind)}>{packageKindLabel(pkg.kind)}</Badge>
              <StatusBadge status={pkg.status} label={tr('billing', `package_status_${pkg.status}`)} tone={packageStatusTone(pkg.status)} />
              <span className="ms-auto text-xs text-muted-foreground">
                {pkg.soldOnThisBill ? tr('billing', 'sold_on_this_bill_for_x', { amount: money(pkg.priceMinor) }) : tr('billing', 'sold_on_another_bill')}
              </span>
            </div>
            {pkg.expiresAt && <p className="text-xs text-muted-foreground">{tr('billing', 'valid_until_x', { date: dateOnly(pkg.expiresAt) })}</p>}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="py-1 text-start font-normal">{tr('billing', 'service')}</th>
                  <th className="py-1 text-end font-normal">{tr('billing', 'included_quantity')}</th>
                  <th className="py-1 text-end font-normal">{tr('billing', 'used')}</th>
                  <th className="py-1 text-end font-normal">{tr('billing', 'left')}</th>
                </tr>
              </thead>
              <tbody>
                {pkg.items.map((item) => (
                  <tr key={item.id} className="border-t">
                    <td className="py-1">{item.description}</td>
                    <td className="py-1 text-end tabular">{item.includedQty}</td>
                    <td className="py-1 text-end tabular">{item.usedQty}</td>
                    <td className="py-1 text-end tabular">{item.remainingQty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <AllowanceUse allowanceMinor={pkg.allowanceMinor ?? 0} usedMinor={pkg.allowanceUsedMinor ?? 0} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
